import { useState } from 'react';
import clsx from 'clsx';

import { ComponentLink } from '~components/ComponentLink';
import { ComponentTrustpilot } from '~components/ComponentTrustpilot';
import { LinkGroup } from '../LinkGroup';
import { Card } from '../Card';

import ChevronDown from '../assets/chevron-down.svg';

import * as styles from './LinkMenu.module.css';

import type {
  ContentfulComponentCardOrContentfulComponentTrustpilot,
  ContentfulComponentLinkMenu,
  ContentfulComponentLinkOrContentfulComponentLinkGroup,
} from '~components/types';

type LinkMenuProps = {
  className?: string;
  style?: React.CSSProperties;
  linkMenu: ContentfulComponentLinkMenu;
  order: number;
};

export const LinkMenu: React.FC<LinkMenuProps> = ({ className, linkMenu, order }) => {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const extraContentComponent = getExtraContentComponent(linkMenu.extraContent);

  return (
    <div
      className={clsx(
        styles.linkMenu,
        styles[`order${order}`],
        styles[`columnCount${getColumnCount(linkMenu)}`],
        mobileOpen && styles.mobileOpen,
        className,
      )}
      onClick={() => {
        // Will enable later!
        // if ((target as HTMLElement).tagName === 'A') setMobileOpen(false);
      }}
    >
      <div
        className={styles.label}
        onClick={() => {
          setMobileOpen(!mobileOpen);
        }}
      >
        {linkMenu.label}
        <ChevronDown className={styles.chevronDown} />
      </div>
      <div className={clsx(styles.contentWrapper)}>
        <div className={styles.content}>
          <div className={styles.columns}>
            <Column column={linkMenu.column1} />
            <Column column={linkMenu.column2} />
            {extraContentComponent && <div className={styles.extraContentWrapper}>{extraContentComponent}</div>}
          </div>
          {linkMenu.link && (
            <ComponentLink link={linkMenu.link} className={styles.linkBar}>
              {linkMenu.link.label}
            </ComponentLink>
          )}
        </div>
      </div>
    </div>
  );
};

type ColumnProps = {
  column: ContentfulComponentLinkOrContentfulComponentLinkGroup[] | null;
};

const Column: React.FC<ColumnProps> = ({ column }) =>
  column ? (
    <div className={styles.column}>
      {column.map((item) => {
        switch (item.__typename) {
          case 'ContentfulComponentLink':
            return (
              <ComponentLink key={item.label} className={styles.link} activeClassName={styles.active} link={item}>
                {item.label}
              </ComponentLink>
            );
          case 'ContentfulComponentLinkGroup':
            return <LinkGroup key={item.label} className={styles.linkGroup} linkGroup={item} />;
          default:
            return null;
        }
      })}
    </div>
  ) : null;

const getColumnCount = (linkMenu: ContentfulComponentLinkMenu): number => {
  let counter: number = 0;
  if (linkMenu.column1.length) counter++;
  if (linkMenu.column2?.length) counter++;
  if (linkMenu.extraContent) counter++;
  return counter;
};

const getExtraContentComponent = (extraContent: ContentfulComponentCardOrContentfulComponentTrustpilot | null) => {
  if (!extraContent) return null;

  switch (extraContent.__typename) {
    case 'ContentfulComponentCard':
      return <Card className={styles.card} card={extraContent} />;
    case 'ContentfulComponentTrustpilot':
      return <ComponentTrustpilot className={styles.card} trustpilot={extraContent} />;
    default:
      return null;
  }
};
